import styled, { createGlobalStyle } from 'styled-components';

import { mediaSmallOrTiny } from '@xing-com/layout-tokens';
import {
  scale010,
  scale005,
  spaceXXS,
  spaceXL,
  spaceXXL,
  xdlColorSelected,
  cornerRadius3XL,
  xdlColorBackground,
  xdlColorCardInspire06,
  xdlColorHoverTertiary,
  dropShadowS,
  xdlColorBackgroundSecondary,
  xdlColorBorderSoft,
} from '@xing-com/tokens';

import {
  CARD_HEIGHT,
  CARD_WIDTH,
  CARD_HIDDEN_HEIGHT,
  CARD_Z_INDEX,
} from './constants';

const TRANSLATEY_FACTOR = 0.1;
const ROTATION_FACTOR = 0.05;
const SCALE_HIDDEN_CARD = 0.12;

const calculateTranslate = (
  currentPosX: number,
  index: number,
  isMobile: boolean,
  direction?: 'left' | 'right' | 'super',
  prepareShuffling?: boolean
): string => {
  if (direction === 'right' || direction === 'super') {
    return `translate(50vw,10vw) rotate(${isMobile ? '10deg' : '30deg'})`;
  } else if (direction === 'left') {
    return ` translate(-50vw,10vw) rotate(${isMobile ? '-10deg' : '-30deg'})`;
  }

  return `scale(${1 - SCALE_HIDDEN_CARD * (index + (prepareShuffling && index === 0 ? 1 : 0))}) translateX(${currentPosX}px) translateY(${Math.abs(currentPosX) * TRANSLATEY_FACTOR}px) rotate(${ROTATION_FACTOR * currentPosX}deg)`;
};

const lightVariables = `
  --cardBackgroundStart: ${xdlColorBackground};
  --cardBackgroundEnd: ${xdlColorCardInspire06};
  --cardBackground: ${xdlColorBackground};
  --cardBorderColor: transparent;
  --cardBorderColorTop: ${xdlColorBackground};
  --cardBorderColorHover: ${xdlColorHoverTertiary};
`;

const darkVariables = `
  --cardBackgroundStart: ${xdlColorBackgroundSecondary};
  --cardBackgroundEnd: ${xdlColorBackground};
  --cardBackground: ${xdlColorBackgroundSecondary};
  --cardBorderColor: ${xdlColorBorderSoft};
  --cardBorderColorTop: ${xdlColorBorderSoft};
  --cardBorderColorHover: ${xdlColorSelected};
`;

const GlobalStyles = createGlobalStyle`
  :root, :root.light {
    ${lightVariables}}
  }
  :root.dark {
    ${darkVariables}
  }
  @media (prefers-color-scheme: dark) {
    :root {
    ${darkVariables}}
    }
  }
`;

const commonCardStyles = ($index: number): string => `
  position: absolute;
  display: flex;
  flex-direction: column;
  width: ${CARD_WIDTH}px;
  height: ${CARD_HEIGHT}px;
  padding: ${spaceXL} ${spaceXXL};
  border-radius: ${cornerRadius3XL};
  box-shadow: ${dropShadowS};
  cursor: pointer;
  transform-origin: bottom center;
  border: 2px solid var(--cardBorderColor);
  border-top: 2px solid var(--cardBorderColorTop);
  transition:
    transform 0.7s ease,
    opacity 0.5s ease,
    top 0.5s ease,
    scale 0.5s ease,
    bottom 0.5s ease;
  touch-action: pan-y;

  &:hover {
    &::after {
      ${
        $index === 0
          ? `
          border-color: var(--cardBorderColorHover);
          border-radius: ${cornerRadius3XL};
          border-style: solid;
          border-width: calc(${scale010} + ${scale005});
          content: '';
          inset: -${spaceXXS};
          pointer-events: none;
          position: absolute;
          `
          : undefined
      }
    }
  }

  z-index: ${CARD_Z_INDEX - $index - 1};
  transform: scale(${1 - SCALE_HIDDEN_CARD * $index});

  bottom: ${2 * CARD_HIDDEN_HEIGHT - CARD_HIDDEN_HEIGHT * $index}px;
  background: ${
    $index > 0
      ? `linear-gradient(
          to bottom,
          var(--cardBackgroundStart) 0%,
          var(--cardBackgroundStart) 95%,
          var(--cardBackgroundEnd) 100%
        )`
      : `var(--cardBackground)`
  };
`;

const Container = styled.div<{
  $direction?: 'left' | 'right' | 'super';
  $currentPos: { x: number; y: number };
  $isSwiping: boolean;
  $index: number;
  $prepareShuffling: boolean;
}>`
  ${({ $index }) => commonCardStyles($index)}

  ${({ $currentPos, $direction, $isSwiping, $index, $prepareShuffling }) => `
    transform: ${calculateTranslate($currentPos.x, $index, false, $direction, $prepareShuffling)};
    ${$direction ? 'pointer-events: none; opacity: 0;' : ''}
    ${$isSwiping && !$direction ? 'transition: none;' : ''}
    ${$isSwiping || $direction ? `z-index: ${CARD_Z_INDEX};` : ''}
    @media ${mediaSmallOrTiny} {
      transform: ${calculateTranslate($currentPos.x, $index, true, $direction)};
    }
    ${$prepareShuffling ? `bottom: 0;` : ''}
  `}
`;

const EmptyState = styled.div`
  background-color: ${xdlColorBackgroundSecondary};
`;

export { Container, EmptyState, GlobalStyles };
